/**
 * Dashboard page - main landing page after login
 * Redesigned with personal finance app inspiration
 */

import { SummaryCard } from '@/components/common/SummaryCard'
import { ExpectationCard } from '@/components/expectations/ExpectationCard'
import { CurrentGoldPrices } from '@/components/prices/CurrentGoldPrices'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { Skeleton } from '@/components/ui/skeleton'
import { useAssets } from '@/services/api/assets'
import { useExpectations } from '@/services/api/expectations'
import { useGoldPrices } from '@/services/api/goldPrice'
import { useSales } from '@/services/api/sales'
import type { GoldPriceHistory } from '@/types/price'
import { formatVND } from '@/utils/currencyUtils'
import { calculatePortfolioSummary } from '@/utils/portfolioUtils'
import {
  IconChartBar,
  IconChartLine,
  IconCoins,
  IconCurrencyDollar,
  IconHistory,
  IconPackage,
  IconSettings,
  IconTarget,
  IconTrendingDown,
  IconTrendingUp,
  IconWallet,
} from '@tabler/icons-react'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

export default function Dashboard() {
  const navigate = useNavigate()
  const { data: assets = [], isLoading: assetsLoading } = useAssets()
  const { data: sales = [], isLoading: salesLoading } = useSales()
  const { data: currentPrices = [], isLoading: pricesLoading } = useGoldPrices('Hồ Chí Minh')

  const isLoading = assetsLoading || salesLoading || pricesLoading

  // Create map of latest prices by gold type ID
  const latestPrices = useMemo(() => {
    const priceMap = new Map<number, GoldPriceHistory>()
    const now = new Date().toISOString()
    const today = new Date().toISOString().split('T')[0] as string

    currentPrices.forEach(price => {
      priceMap.set(price.Id, {
        id: `current_${price.Id}`,
        date: today,
        goldTypeId: price.Id,
        branchName: price.BranchName,
        buyPrice: price.BuyValue,
        sellPrice: price.SellValue,
        createdAt: now,
      })
    })
    return priceMap
  }, [currentPrices])

  const summary = useMemo(() => {
    return calculatePortfolioSummary(assets, sales, latestPrices)
  }, [assets, sales, latestPrices])

  // const holdingAssets = useMemo(() => assets.filter(a => a.status === 'holding').length, [assets])

  // Tính tổng số lượng vàng theo loại cho thẻ "Tổng số Tài sản" (đơn vị: lượng)
  const { totalLuongHolding, quantityBreakdownData } = useMemo(() => {
    const holding = assets.filter(a => a.status === 'holding')
    const totalLuong = holding.reduce((sum, a) => sum + (a.quantity || 0), 0)
    if (totalLuong <= 0) {
      return { totalLuongHolding: 0, quantityBreakdownData: [] }
    }

    const byType = holding.reduce<Record<string, number>>((acc, a) => {
      const name = a.goldTypeName || `Loại #${a.goldTypeId}`
      acc[name] = (acc[name] || 0) + (a.quantity || 0)
      return acc
    }, {})

    const breakdownData = Object.entries(byType)
      .sort((a, b) => b[1] - a[1])
      .map(([name, qtyLuong]) => ({
        name,
        quantity: qtyLuong,
        percent: (qtyLuong / totalLuong) * 100,
      }))

    return {
      totalLuongHolding: totalLuong,
      quantityBreakdownData: breakdownData,
    }
  }, [assets])

  return (
    <div className="container mx-auto px-3 sm:px-4 py-4 sm:py-6 lg:py-8 space-y-6 sm:space-y-8">
      {/* Financial Summary Section */}
      <div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Tổng quan Danh mục</h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">
              Xem tổng quan về tài sản vàng của bạn
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => navigate('/assets')}
              variant="outline"
              size="sm"
              className="flex-1 sm:flex-initial min-h-[44px]"
            >
              <IconPackage className="h-4 w-4 sm:mr-2" stroke={1.5} />
              <span className="hidden sm:inline">Quản lý Tài sản</span>
              <span className="sm:hidden">Tài sản</span>
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            {[1, 2, 3, 4].map(i => (
              <Card key={i}>
                <CardHeader className="pb-3">
                  <Skeleton className="h-4 w-32" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-9 w-40 mb-2" />
                  <Skeleton className="h-3 w-24" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <SummaryCard
              title="Tổng Giá trị Danh mục"
              value={formatVND(summary.currentValue)}
              subtitle="Giá trị hiện tại của tài sản đang nắm giữ"
              icon={IconWallet}
              variant={summary.currentValue > 0 ? 'default' : 'neutral'}
              onClick={() => navigate('/assets')}
            />
            <SummaryCard
              title="Tổng Chi phí"
              value={formatVND(summary.totalCost)}
              subtitle="Tổng số tiền đã đầu tư"
              icon={IconCurrencyDollar}
              variant="neutral"
            />
            <SummaryCard
              title="Lợi nhuận / Lỗ"
              value={formatVND(Math.abs(summary.totalProfitLoss))}
              subtitle="Tổng lợi nhuận hoặc lỗ từ đầu tư"
              change={{
                value: summary.totalProfitLoss,
                percent: summary.profitLossPercent,
              }}
              icon={summary.totalProfitLoss >= 0 ? IconTrendingUp : IconTrendingDown}
              variant={summary.totalProfitLoss >= 0 ? 'positive' : 'negative'}
              onClick={() => navigate('/reports')}
            />
            <SummaryCard
              title="Tỷ lệ Lợi nhuận"
              value={`${summary.profitLossPercent >= 0 ? '+' : ''}${summary.profitLossPercent.toFixed(2)}%`}
              subtitle="Phần trăm lợi nhuận so với chi phí"
              change={{
                value: summary.totalProfitLoss,
                percent: summary.profitLossPercent,
              }}
              icon={summary.profitLossPercent >= 0 ? IconChartLine : IconTrendingDown}
              variant={summary.profitLossPercent >= 0 ? 'positive' : 'negative'}
              onClick={() => navigate('/reports')}
            />
          </div>
        )}

        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
            {[1, 2, 3].map(i => (
              <Card key={i}>
                <CardHeader className="pb-3">
                  <Skeleton className="h-4 w-32" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-9 w-40 mb-2" />
                  <Skeleton className="h-3 w-24" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <SummaryCard
              title="Tổng số Tài sản"
              value={`${totalLuongHolding.toLocaleString('vi-VN', { maximumFractionDigits: 2 })} lượng`}
              icon={IconCoins}
              variant="neutral"
              breakdown={
                quantityBreakdownData.length > 0
                  ? {
                      data: quantityBreakdownData,
                      totalQuantity: totalLuongHolding,
                    }
                  : undefined
              }
            />
            {/* <SummaryCard
              title="Tổng Doanh thu"
              value={formatVND(summary.totalRevenue)}
              subtitle="Từ các giao dịch bán"
              icon={IconCurrencyDollar}
              variant="neutral"
            /> */}
            {/* <SummaryCard
              title="Tài sản Đang nắm giữ"
              value={holdingAssets}
              subtitle={`Tổng giá trị: ${formatVND(summary.currentValue)}`}
              icon={IconPackage}
              variant="default"
              onClick={() => navigate('/assets?filter=holding')}
            /> */}
          </div>
        )}
      </div>

      {/* Current Gold Prices Section */}
      <div>
        <CurrentGoldPrices branchName="Hồ Chí Minh" />
      </div>

      {/* Quick Actions & Navigation */}
      <div>
        <h2 className="text-xl sm:text-2xl font-semibold tracking-tight mb-3 sm:mb-4">Chức năng</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <a href="/assets" className="block">
            <Card className="hover:shadow-md transition-all duration-200 cursor-pointer h-full border-l-4 border-l-primary">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <IconPackage className="h-5 w-5 text-primary" stroke={1.5} />
                  </div>
                  <CardTitle>Quản lý Tài sản</CardTitle>
                </div>
                <CardDescription>Thêm, xem, sửa và xóa các tài sản vàng của bạn</CardDescription>
              </CardHeader>
            </Card>
          </a>
          <a href="/prices" className="block">
            <Card className="hover:shadow-md transition-all duration-200 cursor-pointer h-full border-l-4 border-l-blue-500">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <IconHistory className="h-5 w-5 text-blue-600" stroke={1.5} />
                  </div>
                  <CardTitle>Lịch sử Giá</CardTitle>
                </div>
                <CardDescription>Xem biểu đồ và lịch sử giá vàng theo thời gian</CardDescription>
              </CardHeader>
            </Card>
          </a>
          <a href="/reports" className="block">
            <Card className="hover:shadow-md transition-all duration-200 cursor-pointer h-full border-l-4 border-l-purple-500">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <IconChartBar className="h-5 w-5 text-purple-600" stroke={1.5} />
                  </div>
                  <CardTitle>Báo cáo & Phân tích</CardTitle>
                </div>
                <CardDescription>
                  Xem biểu đồ giá trị tài sản, lợi nhuận/lỗ và báo cáo tổng hợp
                </CardDescription>
              </CardHeader>
            </Card>
          </a>
          <a href="/expectations" className="block">
            <Card className="hover:shadow-md transition-all duration-200 cursor-pointer h-full border-l-4 border-l-orange-500">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-orange-500/10 rounded-lg">
                    <IconTarget className="h-5 w-5 text-orange-600" stroke={1.5} />
                  </div>
                  <CardTitle>Kỳ vọng Giá</CardTitle>
                </div>
                <CardDescription>Thiết lập và theo dõi các mục tiêu giá vàng</CardDescription>
              </CardHeader>
            </Card>
          </a>
          <a href="/settings" className="block">
            <Card className="hover:shadow-md transition-all duration-200 cursor-pointer h-full border-l-4 border-l-gray-500">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-muted rounded-lg">
                    <IconSettings className="h-5 w-5 text-muted-foreground" stroke={1.5} />
                  </div>
                  <CardTitle>Cài đặt</CardTitle>
                </div>
                <CardDescription>Import/Export dữ liệu CSV và cấu hình</CardDescription>
              </CardHeader>
            </Card>
          </a>
        </div>
      </div>

      <DashboardExpectations />
    </div>
  )
}

function DashboardExpectations() {
  const { data: expectations = [] } = useExpectations()
  const { data: currentPrices } = useGoldPrices('Hồ Chí Minh')
  const defaultPrice = currentPrices?.[0]?.SellValue

  if (expectations.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Kỳ vọng Giá</CardTitle>
        </CardHeader>
        <CardContent>
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <IconTarget className="h-12 w-12" stroke={1.5} />
              </EmptyMedia>
              <EmptyTitle>Chưa có kỳ vọng giá nào</EmptyTitle>
              <EmptyDescription>
                Tạo kỳ vọng giá đầu tiên để theo dõi mục tiêu đầu tư của bạn
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button asChild>
                <a href="/expectations">Tạo kỳ vọng đầu tiên</a>
              </Button>
            </EmptyContent>
          </Empty>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Kỳ vọng Giá</CardTitle>
          <a href="/expectations" className="text-sm text-primary hover:text-primary/80">
            Xem tất cả →
          </a>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {expectations.slice(0, 3).map(expectation => (
            <ExpectationCard
              key={expectation.id}
              expectation={expectation}
              {...(defaultPrice !== undefined ? { currentPrice: defaultPrice } : {})}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
